import { useTranslation } from 'react-i18next';

import { KpiAnomaly } from '@/features/insights/api/detect-kpi-anomalies';
import { getAnomalySeverity } from '@/features/insights/utils/anomaly-severity';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { getInitials } from '@/shared/utils/avatar-util';
import { formatDate } from '@/shared/utils/format';

interface AnomaliesProps {
  anomalies?: KpiAnomaly[] | undefined;
  isLoading: boolean;
}

export const Anomalies = ({ anomalies, isLoading }: AnomaliesProps) => {
  const { t } = useTranslation('insight', {
    keyPrefix: 'InsightsHub.Anomalies',
  });
  const { t: sharedTranslation } = useTranslation('shared');

  // Limit to 4 most recent anomalies
  const detectedAnomalies = (
    anomalies?.filter((a) => a.is_anomaly) ?? []
  ).slice(0, 4);

  return (
    <div className="flex h-full w-full flex-col rounded-xl bg-dark-container p-6">
      <h3 className="mb-1 text-base font-semibold">{t('title')}</h3>
      <span className="text-2xs text-primary-shade-grey-500">
        {t('description')}
      </span>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <span className="text-muted-foreground">{t('loading')}</span>
        </div>
      ) : detectedAnomalies.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center">
          <span className="text-muted-foreground">{t('no_anomalies')}</span>
          <span className="text-muted-foreground">
            {t('no_anomalies_description')}
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-4 overflow-y-auto py-6">
          {detectedAnomalies.map((anomaly) => {
            const severityInfo = getAnomalySeverity(
              anomaly.deviation_percentage,
              t,
            );
            if (!severityInfo) return null;

            return (
              <div
                key={anomaly.period}
                className="flex flex-row items-center gap-4 rounded-lg bg-light-container-alt px-6 py-4"
              >
                <div className="flex flex-row items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {getInitials(anomaly.project_name ?? 'AB')}
                    </AvatarFallback>
                    <AvatarImage />
                  </Avatar>
                  <div>
                    <div className="text-sm font-semibold">
                      {anomaly.project_name ??
                        sharedTranslation('unknown_value')}
                    </div>
                    <div className="text-3xs text-custom-grey">
                      {anomaly.org_name ?? sharedTranslation('unknown_value')}
                    </div>
                  </div>
                </div>
                <div className="flex-1 text-2xs">
                  {severityInfo.description}
                </div>

                <span className="text-3xs text-primary-shade-grey-500">
                  {formatDate({ date: anomaly.period, formatType: 'digits' })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
